import { useEffect, useState } from 'react';
import { api, type PlanInfo } from '@/api/client';
import { mockPlan, useMock } from '@/api/mock';

const users = [
  { name: 'John Smith', role: 'Global Admin', email: 'john@contoso.com', mfa: 'Enabled' },
  { name: 'Alice Chen', role: 'Compliance Officer', email: 'alice@contoso.com', mfa: 'Enabled' },
  { name: 'Bob Miller', role: 'Security Officer', email: 'bob@contoso.com', mfa: 'Enabled' },
];

export function SettingsPage() {
  const [plan, setPlan] = useState<PlanInfo | null>(null);

  useEffect(() => {
    if (useMock()) {
      setPlan(mockPlan);
      return;
    }
    api.currentPlan().then(setPlan).catch(() => setPlan(mockPlan));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-sm text-ink-muted mt-1">Tenant configuration, user administration, subscriptions, and billing.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card space-y-2">
          <div className="card-title">Subscription</div>
          <div className="text-sm text-ink-muted">Tenant: <span className="text-ink">{plan?.name ?? '-'}</span></div>
          <div className="text-sm text-ink-muted">Plan: <span className="text-ink">{plan?.displayName ?? '-'}</span> ({plan?.planId ?? '-'})</div>
          <div className="text-sm text-ink-muted">Seats: <span className="text-ink">{plan?.seats ?? '-'}</span></div>
          <div className="text-sm text-ink-muted">GPU Quota: <span className="text-ink">{plan?.maxGpuCount ?? '-'}</span></div>
          <div className="text-sm text-ink-muted">Price / Seat: <span className="text-ink">${plan?.monthlyPricePerSeat ?? '-'}</span> / month</div>
          <div className="pt-2">
            <button className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm hover:bg-accent/20">Open Marketplace Subscription</button>
          </div>
        </div>

        <div className="card space-y-2">
          <div className="card-title">Tenant Preferences</div>
          <label className="block text-sm text-ink-muted">Default Data Region</label>
          <select className="w-full rounded-md bg-bg-soft border border-border px-3 py-2 text-sm">
            <option>East US</option>
            <option>West Europe</option>
            <option>Japan East</option>
          </select>
          <label className="block text-sm text-ink-muted mt-2">Compliance Baseline</label>
          <select className="w-full rounded-md bg-bg-soft border border-border px-3 py-2 text-sm">
            <option>ISO 27001 + SOC 2</option>
            <option>GDPR + ISO 27018</option>
            <option>HIPAA + NIST CSF</option>
          </select>
          <div className="pt-2">
            <button className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-bg-soft">Save Preferences</button>
          </div>
        </div>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">User Management</div>
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Role</th>
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">MFA</th>
              <th className="text-left py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email} className="border-b border-border/50 last:border-0">
                <td className="py-2">{u.name}</td>
                <td className="py-2">{u.role}</td>
                <td className="py-2">{u.email}</td>
                <td className="py-2"><span className="badge-good">{u.mfa}</span></td>
                <td className="py-2"><button className="text-accent-cyan hover:underline">Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
